import React, { useState, useEffect } from 'react';
import './PWANotificationSetup.css';
import pwaNotificationService from '../services/pwaNotifications';
import ServiceWorkerDebug from './ServiceWorkerDebug';

const PWANotificationSetup = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    initializePWA();
  }, []);

  const initializePWA = async () => {
    try {
      console.log('Initializing PWA notifications...');
      console.log('Service Worker support:', 'serviceWorker' in navigator);
      console.log('Push Manager support:', 'PushManager' in window);
      console.log('Secure context:', window.isSecureContext);
      console.log('Protocol:', window.location.protocol);
      
      const success = await pwaNotificationService.initialize();
      setIsSupported(success);
      
      if (success) {
        console.log('PWA initialization successful');
        const status = await pwaNotificationService.checkSubscriptionStatus();
        setIsSubscribed(status.subscribed);
        
        // Show setup if not subscribed and not previously dismissed
        if (!status.subscribed && !localStorage.getItem('pwa-notifications-dismissed')) {
          setShowSetup(true);
        }
      } else {
        console.log('PWA initialization failed');
        // Show helpful error message for HTTPS requirement
        if (!window.isSecureContext && window.location.hostname !== 'localhost') {
          setError('Push notifications require HTTPS on mobile devices. Please use HTTPS or test on localhost.');
        } else {
          setError('Service worker registration failed. Check browser console for details.');
        }
      }
    } catch (error) {
      console.error('Error initializing PWA:', error);
      setError('Failed to initialize push notifications: ' + error.message);
    }
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await pwaNotificationService.subscribeToPushNotifications();
      setIsSubscribed(true);
      setShowSetup(false);
      
      // Show success message
      if (pwaNotificationService.registration) {
        await pwaNotificationService.showLocalNotification(
          'Notifications Enabled!',
          'You will now receive push notifications for job updates.',
          { type: 'success' }
        );
      }
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      setError(error.message || 'Failed to enable notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await pwaNotificationService.unsubscribeFromPushNotifications();
      setIsSubscribed(false);
    } catch (error) {
      console.error('Error unsubscribing from notifications:', error);
      setError(error.message || 'Failed to disable notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowSetup(false);
    localStorage.setItem('pwa-notifications-dismissed', 'true');
  };

  const handleTestNotification = async () => {
    try {
      await pwaNotificationService.showLocalNotification(
        'Test Notification',
        'This is a test notification from Paragon!',
        { type: 'test' }
      );
    } catch (error) {
      setError('Failed to show test notification');
    }
  };

  // Always show the component for debugging purposes
  return (
    <div className="pwa-notification-setup">
      {!isSupported && (
        <div className="pwa-not-supported-banner">
          <div className="not-supported-content">
            <div className="not-supported-icon">⚠️</div>
            <div className="not-supported-text">
              <h4>PWA Push Notifications Not Supported</h4>
              <p>Push notifications are not available in this environment. Check the debug information below for details.</p>
            </div>
          </div>
        </div>
      )}
      
      {showSetup && (
        <div className="pwa-setup-banner">
          <div className="setup-content">
            <div className="setup-icon">🔔</div>
            <div className="setup-text">
              <h4>Enable Push Notifications</h4>
              <p>Get notified instantly when jobs change, even when the app is closed!</p>
            </div>
            <div className="setup-actions">
              <button 
                className="enable-btn"
                onClick={handleSubscribe}
                disabled={isLoading}
              >
                {isLoading ? 'Enabling...' : 'Enable'}
              </button>
              <button 
                className="dismiss-btn"
                onClick={handleDismiss}
                disabled={isLoading}
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="pwa-status">
        <div className="status-info">
          <span className="status-icon">
            {isSubscribed ? '🔔' : '🔕'}
          </span>
          <span className="status-text">
            Push Notifications: {isSubscribed ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        
        <div className="status-actions">
          {isSubscribed ? (
            <>
              <button 
                className="test-btn"
                onClick={handleTestNotification}
                disabled={isLoading}
              >
                Test
              </button>
              <button 
                className="disable-btn"
                onClick={handleUnsubscribe}
                disabled={isLoading}
              >
                {isLoading ? 'Disabling...' : 'Disable'}
              </button>
            </>
          ) : (
            <button 
              className="enable-btn"
              onClick={handleSubscribe}
              disabled={isLoading}
            >
              {isLoading ? 'Enabling...' : 'Enable'}
            </button>
          )}
        </div>
      </div>

      {/* Debug Information */}
      <div className="debug-info">
        <h4>🔧 Debug Information</h4>
        <div className="debug-grid">
          <div className="debug-item">
            <span className="debug-label">Protocol:</span>
            <span className="debug-value">{window.location.protocol}</span>
          </div>
          <div className="debug-item">
            <span className="debug-label">Secure Context:</span>
            <span className={`debug-value ${window.isSecureContext ? 'secure' : 'insecure'}`}>
              {window.isSecureContext ? '✅ Yes' : '❌ No'}
            </span>
          </div>
          <div className="debug-item">
            <span className="debug-label">Service Worker:</span>
            <span className={`debug-value ${'serviceWorker' in navigator ? 'supported' : 'not-supported'}`}>
              {'serviceWorker' in navigator ? '✅ Supported' : '❌ Not Supported'}
            </span>
          </div>
          <div className="debug-item">
            <span className="debug-label">Push Manager:</span>
            <span className={`debug-value ${'PushManager' in window ? 'supported' : 'not-supported'}`}>
              {'PushManager' in window ? '✅ Supported' : '❌ Not Supported'}
            </span>
          </div>
          <div className="debug-item">
            <span className="debug-label">PWA Support:</span>
            <span className={`debug-value ${isSupported ? 'supported' : 'not-supported'}`}>
              {isSupported ? '✅ Supported' : '❌ Not Supported'}
            </span>
          </div>
        </div>
        
        {!window.isSecureContext && window.location.hostname !== 'localhost' && (
          <div className="debug-warning">
            ⚠️ <strong>HTTPS Required:</strong> Push notifications require HTTPS on mobile devices. 
            Use HTTPS or test on localhost.
          </div>
        )}
        
      </div>
      
      {/* Debug Component */}
      <ServiceWorkerDebug />
    </div>
  );
};

export default PWANotificationSetup;
